import { useState, useEffect } from 'react';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Package, 
  Search, 
  QrCode, 
  Eye, 
  Download, 
  AlertTriangle,
  Filter,
  MapPin,
  Zap,
  Loader2,
  Layers,
  List,
  Clock,
  DollarSign
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';
import { useAuth } from '@/hooks/use-auth';
import { useLocation } from '@/contexts/LocationContext';
import { useLocations } from '@/hooks/use-locations';
import { productService } from '@/lib/packages';

type Product = Database['public']['Tables']['products']['Row'] & {
  brand?: string;
  location?: string;
  energyClass?: string;
  criticalStock: number;
};

// Helper functions
const formatPrice = (price: number) => `₺${price.toLocaleString('tr-TR')}`;

const getProductMeta = (meta: any) => {
  if (!meta) return {};
  return {
    brand: meta.brand || '',
    location: meta.location || '',
    energyClass: meta.energyClass || '',
    criticalStock: meta.criticalStock || 5
  };
};

const Products = () => {
  const navigate = useNavigate();
  const { companyId } = useAuth();
  const { currentLocation, availableLocations } = useLocation();
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('Tümü');
  const [selectedBrand, setSelectedBrand] = useState('Tümü');
  const [showCriticalOnly, setShowCriticalOnly] = useState(false);
  const [hideOutOfStock, setHideOutOfStock] = useState(true); // Default: hide out of stock items
  const [showAllLocations] = useState(true);
  const [selectedLocationId] = useState<string>('all');
  const [showCategoryView, setShowCategoryView] = useState(false);
  const [selectedCategoryView, setSelectedCategoryView] = useState<string>('Tümü');
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [categories, setCategories] = useState<string[]>(['Tümü']);
  const [brands, setBrands] = useState<string[]>(['Tümü']);
  const [onHoldCounts, setOnHoldCounts] = useState<Record<string, number>>({});

  // Load products from database (uses pagination to handle >1000 rows)
  useEffect(() => {
    const fetchProducts = async () => {
      if (!companyId) return;
      
      try {
        setLoading(true);
        setError(null);
        
        // Determine location filter
        let locationId: string | undefined;
        if (!showAllLocations && currentLocation && selectedLocationId === 'current') {
          locationId = currentLocation.id;
        } else if (!showAllLocations && selectedLocationId !== 'current' && selectedLocationId !== 'all') {
          locationId = selectedLocationId;
        }
        
        // Use paginated getAllProducts to fetch all products
        const allProducts = await productService.getAllProducts(companyId, {
          locationId,
          showAllLocations,
          includeInactive: false
        });
        
        // Fetch all locations once (much more efficient than N queries)
        const locationIds = [...new Set(allProducts.map(p => p.location_id).filter(Boolean))] as string[];
        const locationMap = new Map<string, { id: string; name: string }>();
        
        if (locationIds.length > 0) {
          // Fetch all locations in one query
          const { data: locationsData } = await supabase
            .from('locations')
            .select('id, name')
            .in('id', locationIds);
          
          if (locationsData) {
            locationsData.forEach(loc => {
              locationMap.set(loc.id, loc);
            });
          }
        }
        
        // Map locations to products using the pre-fetched map
        const productsWithLocations = allProducts.map((product) => {
          const location = product.location_id ? locationMap.get(product.location_id) : undefined;
          return {
            ...product,
            location
          };
        });
        
        // Transform database products to include additional fields
        const transformedProducts: Product[] = productsWithLocations.map(product => ({
          ...product,
          ...getProductMeta(product.meta),
          criticalStock: getProductMeta(product.meta).criticalStock
        }));
        
        setProducts(transformedProducts);
        
        // Extract unique categories and brands (filter out empty strings and null values)
        const uniqueCategories = ['Tümü', ...new Set(
          transformedProducts
            .map(p => p.category)
            .filter((cat): cat is string => Boolean(cat) && cat.trim() !== '')
        )];
        const uniqueBrands = ['Tümü', ...new Set(
          transformedProducts
            .map(p => getProductMeta(p.meta).brand)
            .filter((brand): brand is string => Boolean(brand) && brand.trim() !== '')
        )];
        
        setCategories(uniqueCategories);
        setBrands(uniqueBrands);

        // Fetch on-hold sales count for all products
        if (transformedProducts.length > 0) {
          const productIds = transformedProducts.map(p => p.id);
          
          // Chunk product IDs to avoid URL length limits (100 IDs per chunk)
          const chunkSize = 100;
          const chunks: string[][] = [];
          for (let i = 0; i < productIds.length; i += chunkSize) {
            chunks.push(productIds.slice(i, i + chunkSize));
          }
          
          // Fetch ONLY pending on-hold records (status = 'on_hold') for these products in chunks
          const allOnHoldData: any[] = [];
          
          for (const chunk of chunks) {
            const { data: chunkData, error: chunkError } = await supabase
              .from('on_holds')
              .select('product_id, hold_quantity')
              .eq('company_id', companyId)
              .eq('status', 'on_hold')
              .in('product_id', chunk);
            
            if (chunkError) {
              console.error('Error fetching on-hold chunk:', chunkError);
              // Continue with other chunks even if one fails
              continue;
            }
            
            if (chunkData) {
              allOnHoldData.push(...chunkData);
            }
          }

          // Count only pending on-hold items per product (sum of hold_quantity)
          const counts: Record<string, number> = {};
          allOnHoldData.forEach(onHold => {
            if (onHold.product_id) {
              if (!counts[onHold.product_id]) {
                counts[onHold.product_id] = 0;
              }
              // Sum the hold_quantity for each pending on-hold record
              counts[onHold.product_id] += onHold.hold_quantity || 1;
            }
          });
          setOnHoldCounts(counts);
        }
        
      } catch (err) {
        console.error('Error fetching products:', err);
        setError('Ürünler yüklenirken bir hata oluştu');
      } finally {
        setLoading(false);
      }
    };
    
    void fetchProducts();
  }, [companyId, currentLocation, showAllLocations, selectedLocationId]);

  const handleProductSelect = (productId: string) => {
    setSelectedProducts(prev => 
      prev.includes(productId)
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  const handleSelectAll = () => {
    if (selectedProducts.length === filteredProducts.length) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(filteredProducts.map(p => p.id));
    }
  };

  const filteredProducts = products.filter(product => {
    const productMeta = getProductMeta(product.meta);
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.item_code.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'Tümü' || product.category === selectedCategory;
    const matchesBrand = selectedBrand === 'Tümü' || productMeta.brand === selectedBrand;
    const matchesCritical = !showCriticalOnly || product.quantity_on_hand <= productMeta.criticalStock;
    const matchesStock = !hideOutOfStock || product.quantity_on_hand > 0; // Hide out of stock items when enabled
    
    return matchesSearch && matchesCategory && matchesBrand && matchesCritical && matchesStock;
  });

  const getStockStatus = (product: Product) => {
    const productMeta = getProductMeta(product.meta);
    if (product.quantity_on_hand < 0) {
      return { label: 'Eksi Stok', color: 'bg-red-100 text-red-800 border border-red-300' };
    }
    if (product.quantity_on_hand === 0) {
      return { label: 'Tükendi', color: 'bg-destructive/20 text-destructive' };
    }
    if (product.quantity_on_hand <= productMeta.criticalStock) {
      return { label: 'Kritik', color: 'bg-warning/20 text-warning' };
    }
    return { label: 'Stokta', color: 'bg-success/20 text-success' };
  };

  const handlePrintQR = async (productIds: string[]) => {
    if (productIds.length === 0) return;
    
    try {
      const selectedProductsData = products.filter(p => productIds.includes(p.id));
      
      if (selectedProductsData.length === 0) {
        console.error('No products found for selected IDs');
        return;
      }

      // For QR Yazdır button - export all selected products to Excel
      const { exportProductsToExcel } = await import('@/lib/qr-utils');
      
      const qrProductsData = selectedProductsData.map(product => ({
        id: product.id,
        name: product.name,
        item_code: product.item_code,
        brand: getProductMeta(product.meta).brand,
        company_id: companyId!
      }));

      await exportProductsToExcel(qrProductsData, companyId!);
      
      console.log('QR Excel exported for:', productIds.length, 'products');
    } catch (error) {
      console.error('Error exporting QR codes:', error);
    }
  };

  const handleIndividualQR = async (product: Product) => {
    try {
      // Generate individual QR code with branding
      const { generateBrandedQRCode, createPrintableQRLabel } = await import('@/lib/qr-utils');
      
      const qrProductData = {
        id: product.id,
        name: product.name,
        item_code: product.item_code,
        brand: getProductMeta(product.meta).brand,
        company_id: companyId!
      };

      const qrCodeDataUrl = await generateBrandedQRCode(qrProductData);
      
      if (qrCodeDataUrl) {
        // Create a printable label with branding
        const labelHTML = createPrintableQRLabel(qrProductData, qrCodeDataUrl, {
          includeBranding: true,
          labelSize: 'medium'
        });

        // Open print window
        const printWindow = window.open('', '_blank');
        if (printWindow) {
          printWindow.document.write(`
            <!DOCTYPE html>
            <html>
              <head>
                <title>QR Kodu - ${product.name}</title>
                <style>
                  @page { margin: 10mm; }
                  body { 
                    font-family: Arial, sans-serif; 
                    margin: 0; 
                    padding: 10mm;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    min-height: 80vh;
                  }
                </style>
              </head>
              <body>
                ${labelHTML}
              </body>
            </html>
          `);
          printWindow.document.close();
          printWindow.focus();
          printWindow.print();
        }
      }
    } catch (error) {
      console.error('Error generating individual QR code:', error);
    }
  };

  const handleExport = () => {
    console.log('Export selected products:', selectedProducts);
  };

  const handleViewProduct = (productId: string) => {
    navigate(`/products/${productId}`);
  };

  // Category view logic
  const getCategoryViewProducts = () => {
    // Filter products: exclude 0 qty, apply search and other filters
    const baseFiltered = products.filter(product => {
      const productMeta = getProductMeta(product.meta);
      const productLocation = (product as any).location;
      const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           product.item_code.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesBrand = selectedBrand === 'Tümü' || productMeta.brand === selectedBrand;
      const matchesCritical = !showCriticalOnly || product.quantity_on_hand <= productMeta.criticalStock;
      const hasStock = product.quantity_on_hand > 0; // Always hide 0 qty in category view
      
      // Location filter - use location_id directly for more reliable matching
      let matchesLocation = true;
      if (!showAllLocations && currentLocation && selectedLocationId === 'current') {
        matchesLocation = product.location_id === currentLocation.id;
      } else if (!showAllLocations && selectedLocationId !== 'current' && selectedLocationId !== 'all') {
        matchesLocation = product.location_id === selectedLocationId;
      }
      // If showAllLocations is true, matchesLocation stays true (show all)
      
      return matchesSearch && matchesBrand && matchesCritical && hasStock && matchesLocation;
    });

    // Group by category
    const groupedByCategory = baseFiltered.reduce((acc, product) => {
      const category = product.category || 'Kategorisiz';
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(product);
      return acc;
    }, {} as Record<string, Product[]>);

    // Sort products within each category by quantity (descending)
    Object.keys(groupedByCategory).forEach(category => {
      groupedByCategory[category].sort((a, b) => b.quantity_on_hand - a.quantity_on_hand);
    });

    // Filter by selected category if not "Tümü"
    if (selectedCategoryView !== 'Tümü') {
      return { [selectedCategoryView]: groupedByCategory[selectedCategoryView] || [] };
    }

    return groupedByCategory;
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center">
              <Package className="w-6 h-6 text-accent" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Ürün Listesi</h1>
              <p className="text-muted-foreground">
                {filteredProducts.length} ürün • {products.filter(p => p.quantity_on_hand <= getProductMeta(p.meta).criticalStock).length} kritik stok
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant={showCategoryView ? "default" : "outline"}
              size="default"
              onClick={() => {
                setShowCategoryView(!showCategoryView);
                // Reset category filter when switching views
                if (!showCategoryView) {
                  setSelectedCategoryView('Tümü');
                }
              }}
              className={showCategoryView ? "bg-primary text-primary-foreground shadow-md" : "border-2 border-primary hover:bg-primary/10"}
            >
              {showCategoryView ? (
                <>
                  <List className="w-4 h-4 mr-2" />
                  Normal Görünüm
                </>
              ) : (
                <>
                  <Layers className="w-4 h-4 mr-2" />
                  Kategorisel Geniş Bak
                </>
              )}
            </Button>

            {selectedProducts.length > 0 && (
              <>
                <span className="text-sm text-muted-foreground">
                  {selectedProducts.length} seçili
                </span>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => handlePrintQR(selectedProducts)}
                  className="gap-2"
                >
                  <QrCode className="w-4 h-4" />
                  QR Yazdır
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleExport}
                  className="gap-2"
                >
                  <Download className="w-4 h-4" />
                  Dışa Aktar
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Ürün ara..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categories
                    .filter(category => category && category.trim() !== '')
                    .map(category => (
                      <SelectItem key={category} value={category}>
                        {category}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>

              <Select value={selectedBrand} onValueChange={setSelectedBrand}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {brands
                    .filter(brand => brand && brand.trim() !== '')
                    .map(brand => (
                      <SelectItem key={brand} value={brand}>
                        {brand}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>

              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="hide-out-of-stock"
                    checked={hideOutOfStock}
                    onCheckedChange={(checked) => setHideOutOfStock(checked === true)}
                  />
                  <label htmlFor="hide-out-of-stock" className="text-sm font-medium cursor-pointer">
                    Tükenenleri Gizle
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="critical-stock"
                    checked={showCriticalOnly}
                    onCheckedChange={(checked) => setShowCriticalOnly(checked === true)}
                  />
                  <label htmlFor="critical-stock" className="text-sm font-medium cursor-pointer">
                    Sadece Kritik Stok
                  </label>
                </div>
              </div>
            </div>

          </CardContent>
        </Card>

        {/* Bulk Selection */}
        <Card>
          <CardContent className="py-4">
            <div className="flex items-center gap-3">
              <Checkbox
                checked={selectedProducts.length === filteredProducts.length && filteredProducts.length > 0}
                onCheckedChange={handleSelectAll}
              />
              <span className="text-sm font-medium">Tümünü Seç</span>
              {selectedProducts.length > 0 && (
                <span className="text-sm text-muted-foreground">
                  ({selectedProducts.length}/{filteredProducts.length})
                </span>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Loading State */}
        {loading && (
          <Card>
            <CardContent className="py-16">
              <div className="text-center">
                <Loader2 className="w-16 h-16 mx-auto text-muted-foreground mb-4 animate-spin" />
                <h3 className="text-xl font-semibold mb-2">Ürünler Yükleniyor</h3>
                <p className="text-muted-foreground">
                  Lütfen bekleyin...
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Error State */}
        {error && (
          <Card>
            <CardContent className="py-16">
              <div className="text-center">
                <AlertTriangle className="w-16 h-16 mx-auto text-destructive mb-4" />
                <h3 className="text-xl font-semibold mb-2">Hata Oluştu</h3>
                <p className="text-muted-foreground mb-4">{error}</p>
                <Button onClick={() => window.location.reload()}>Tekrar Dene</Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Category View */}
        {!loading && !error && showCategoryView && (() => {
          const categoryGroups = getCategoryViewProducts();
          const categoryList = Object.keys(categoryGroups).sort();
          
          return (
            <div className="space-y-6">
              {/* Category Filter */}
              <Card>
                <CardContent className="p-4">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
                    <label className="text-sm font-medium whitespace-nowrap">Kategori:</label>
                    <Select value={selectedCategoryView} onValueChange={setSelectedCategoryView}>
                      <SelectTrigger className="w-full sm:w-[200px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Tümü">Tümü</SelectItem>
                        {categoryList
                          .filter(category => category && category.trim() !== '')
                          .map(category => (
                            <SelectItem key={category} value={category}>
                              {category}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                    <span className="text-sm text-muted-foreground sm:ml-auto">
                      {Object.values(categoryGroups).flat().length} ürün
                    </span>
                  </div>
                </CardContent>
              </Card>

              {/* Category Groups */}
              {categoryList.length === 0 ? (
                <Card>
                  <CardContent className="py-16">
                    <div className="text-center">
                      <Package className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                      <h3 className="text-xl font-semibold mb-2">Ürün Bulunamadı</h3>
                      <p className="text-muted-foreground">
                        Arama kriterlerinize uygun ürün bulunamadı.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                categoryList.map(category => {
                  const categoryProducts = categoryGroups[category];
                  const totalQty = categoryProducts.reduce((sum, p) => sum + (p.quantity_on_hand || 0), 0);
                  
                  return (
                    <Card key={category}>
                      <CardHeader>
                        <CardTitle className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                          <span>{category}</span>
                          <Badge variant="secondary" className="text-sm w-fit">
                            {categoryProducts.length} ürün • Toplam: {totalQty} adet
                          </Badge>
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          {categoryProducts.map((product) => {
                            const productMeta = getProductMeta(product.meta);
                            const stockStatus = getStockStatus(product);
                            
                            return (
                              <div
                                key={product.id}
                                className="flex flex-col sm:flex-row sm:items-center justify-between p-3 border rounded-lg hover:bg-accent/50 transition-colors gap-2"
                              >
                                <div className="flex-1 min-w-0">
                                  <div className="flex flex-wrap items-center gap-2 mb-1">
                                    <span className="font-medium text-sm truncate">{product.name}</span>
                                    {productMeta.brand && (
                                      <Badge variant="outline" className="text-xs">
                                        {productMeta.brand}
                                      </Badge>
                                    )}
                                  </div>
                                  <div className="flex flex-wrap items-center gap-2 sm:gap-3 text-xs text-muted-foreground">
                                    <span className="font-mono">{product.item_code}</span>
                                    <span className="text-accent font-semibold">{formatPrice(product.price)}</span>
                                    {onHoldCounts[product.id] && onHoldCounts[product.id] > 0 && (
                                      <span className="flex items-center gap-1">
                                        <Clock className="w-3 h-3" />
                                        Emanet: {onHoldCounts[product.id]} adet bekleyen
                                      </span>
                                    )}
                                    {(() => {
                                      const currentCost = typeof product.current_cost === 'string' 
                                        ? parseFloat(product.current_cost) 
                                        : (product.current_cost ?? null);
                                      const initialCost = typeof product.initial_cost === 'string' 
                                        ? parseFloat(product.initial_cost) 
                                        : (product.initial_cost ?? 0);
                                      const cost = currentCost ?? initialCost;
                                      const quantity = product.quantity_on_hand > 0 ? product.quantity_on_hand : 0;
                                      const totalCost = cost * quantity;
                                      
                                      if (cost !== null && cost !== undefined && !isNaN(cost)) {
                                        return (
                                          <span className="flex items-center gap-1">
                                            <DollarSign className="w-3 h-3" />
                                            Maliyet: {formatPrice(totalCost)}
                                          </span>
                                        );
                                      }
                                      return null;
                                    })()}
                                  </div>
                                </div>
                                <div className="flex flex-col items-end gap-2 sm:ml-4">
                                  <div className="flex flex-col items-end gap-2">
                                    {/* Stock Quantity */}
                                    <div className="flex items-center gap-2">
                                      <Badge variant="secondary" className={stockStatus.color}>
                                        {stockStatus.label}
                                      </Badge>
                                      <span className="font-bold text-base sm:text-lg min-w-[60px] text-right">
                                        Stok: {product.quantity_on_hand} adet
                                      </span>
                                    </div>
                                    
                                    {/* Total Cost */}
                                    {(() => {
                                      const currentCost = typeof product.current_cost === 'string' 
                                        ? parseFloat(product.current_cost) 
                                        : (product.current_cost ?? null);
                                      const initialCost = typeof product.initial_cost === 'string' 
                                        ? parseFloat(product.initial_cost) 
                                        : (product.initial_cost ?? 0);
                                      const cost = currentCost ?? initialCost;
                                      const quantity = product.quantity_on_hand > 0 ? product.quantity_on_hand : 0;
                                      const totalCost = cost * quantity;
                                      
                                      if (cost !== null && cost !== undefined && !isNaN(cost)) {
                                        return (
                                          <div className="flex items-center gap-1">
                                            <DollarSign className="w-3 h-3 text-muted-foreground" />
                                            <span className="font-bold text-base sm:text-lg">
                                              Maliyet: {formatPrice(totalCost)}
                                            </span>
                                          </div>
                                        );
                                      }
                                      return null;
                                    })()}
                                    
                                    {/* On-hold Quantity */}
                                    {onHoldCounts[product.id] && onHoldCounts[product.id] > 0 && (
                                      <div className="flex items-center gap-1">
                                        <Clock className="w-3 h-3 text-muted-foreground" />
                                        <span className="font-bold text-base sm:text-lg">
                                          Emanet: {onHoldCounts[product.id]} adet bekleyen
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                  
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleViewProduct(product.id)}
                                    className="h-8 w-8 p-0"
                                  >
                                    <Eye className="w-4 h-4" />
                                  </Button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })
              )}
            </div>
          );
        })()}

        {/* Products Grid */}
        {!loading && !error && !showCategoryView && (
        <div className="grid gap-4">
          {filteredProducts.map((product) => {
            const stockStatus = getStockStatus(product);
            const productMeta = getProductMeta(product.meta);
            const productLocation = (product as any).location; // Location data from join
            const isFromDifferentLocation = showAllLocations && productLocation && currentLocation && productLocation.id !== currentLocation.id;
            
            const isNegativeStock = product.quantity_on_hand < 0;
            
            return (
              <Card key={product.id} className={`hover:shadow-md transition-shadow ${
                isNegativeStock 
                  ? 'border-red-300 bg-red-50/50' 
                  : isFromDifferentLocation 
                    ? 'border-yellow-300 bg-yellow-50/30' 
                    : ''
              }`}>
                <CardContent className="p-6">
                  {/* Negative Stock Warning Banner */}
                  {isNegativeStock && (
                    <div className="mb-4 p-3 bg-red-100 border border-red-300 rounded-md">
                      <div className="flex items-center gap-2 text-sm text-red-800">
                        <AlertTriangle className="w-4 h-4" />
                        <span className="font-medium">Uyarı: Bu ürün eksi stokta! Mevcut stok: {product.quantity_on_hand}</span>
                      </div>
                    </div>
                  )}
                  
                  {/* Location Warning Banner */}
                  {isFromDifferentLocation && (
                    <div className="mb-4 p-2 bg-yellow-100 border border-yellow-300 rounded-md">
                      <div className="flex items-center gap-2 text-sm text-yellow-800">
                        <AlertTriangle className="w-4 h-4" />
                        <span className="font-medium">Farklı Lokasyon:</span>
                        <span>{productLocation.name}</span>
                        <span className="text-yellow-600">- Bu ürün mevcut lokasyonunuzda bulunmuyor</span>
                      </div>
                    </div>
                  )}
                  
                  <div className="flex items-start gap-4">
                    <Checkbox
                      checked={selectedProducts.includes(product.id)}
                      onCheckedChange={() => handleProductSelect(product.id)}
                    />

                    <div className="flex-1 min-w-0">
                      <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-start gap-2 mb-2">
                            <h3 className="font-semibold text-lg leading-tight">{product.name}</h3>
                            {productMeta.energyClass && (
                              <Badge variant="secondary" className="bg-green-500/20 text-green-600">
                                <Zap className="w-3 h-3 mr-1" />
                                {productMeta.energyClass}
                              </Badge>
                            )}
                            {isFromDifferentLocation && (
                              <Badge variant="secondary" className="bg-yellow-500/20 text-yellow-700">
                                <MapPin className="w-3 h-3 mr-1" />
                                Farklı Lokasyon
                              </Badge>
                            )}
                          </div>
                          
                          <div className="flex flex-wrap gap-4 text-sm text-muted-foreground mb-3">
                            <span>Kod: <span className="font-mono">{product.item_code}</span></span>
                            {productMeta.brand && <span>Marka: {productMeta.brand}</span>}
                            <span>Kategori: {product.category}</span>
                            {productLocation && (
                              <span className="flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                {productLocation.name}
                              </span>
                            )}
                            {productMeta.location && (
                              <span className="flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                {productMeta.location}
                              </span>
                            )}
                          </div>

                          <div className="flex flex-col gap-3">
                            {/* Price */}
                            <div className="text-lg font-bold text-accent">
                              {formatPrice(product.price)}
                            </div>
                            
                            {/* Key Metrics - Separated clearly */}
                            <div className="flex flex-wrap items-center gap-4">
                              {/* Stock Quantity */}
                              <div className="flex items-center gap-2">
                                <Badge variant="secondary" className={stockStatus.color}>
                                  {stockStatus.label}
                                </Badge>
                                <span className={`text-sm font-medium ${isNegativeStock ? 'font-bold text-red-700' : ''}`}>
                                  Stok: {product.quantity_on_hand} adet
                                </span>
                                {(product.quantity_on_hand < 0 || product.quantity_on_hand <= productMeta.criticalStock) && (
                                  <AlertTriangle className={`w-4 h-4 ${product.quantity_on_hand < 0 ? 'text-red-600' : 'text-warning'}`} />
                                )}
                              </div>
                              
                              {/* Total Cost */}
                              {(() => {
                                const currentCost = typeof product.current_cost === 'string' 
                                  ? parseFloat(product.current_cost) 
                                  : (product.current_cost ?? null);
                                const initialCost = typeof product.initial_cost === 'string' 
                                  ? parseFloat(product.initial_cost) 
                                  : (product.initial_cost ?? 0);
                                const cost = currentCost ?? initialCost;
                                const quantity = product.quantity_on_hand > 0 ? product.quantity_on_hand : 0;
                                const totalCost = cost * quantity;
                                
                                if (cost !== null && cost !== undefined && !isNaN(cost)) {
                                  return (
                                    <div className="flex items-center gap-2">
                                      <DollarSign className="w-4 h-4 text-muted-foreground" />
                                      <span className="text-sm font-medium">
                                        Maliyet: {formatPrice(totalCost)}
                                      </span>
                                    </div>
                                  );
                                }
                                return null;
                              })()}
                              
                              {/* On-hold Quantity */}
                              {onHoldCounts[product.id] && onHoldCounts[product.id] > 0 && (
                                <div className="flex items-center gap-2">
                                  <Clock className="w-4 h-4 text-muted-foreground" />
                                  <span className="text-sm font-medium">
                                    Emanet: {onHoldCounts[product.id]} adet bekleyen
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleIndividualQR(product)}
                            className="gap-2"
                          >
                            <QrCode className="w-4 h-4" />
                            QR
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleViewProduct(product.id)}
                            className="gap-2"
                          >
                            <Eye className="w-4 h-4" />
                            Detay
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
        )}

        {!loading && !error && !showCategoryView && filteredProducts.length === 0 && (
          <Card>
            <CardContent className="py-16">
              <div className="text-center">
                <Package className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-xl font-semibold mb-2">Ürün Bulunamadı</h3>
                <p className="text-muted-foreground">
                  Arama kriterlerinize uygun ürün bulunamadı.
                </p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default Products;